import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Select, SelectProps } from '@mantine/core';
import {
  normalizeLocationTypeKey,
  resolveLocationTypeInput,
} from '../features/schedule-locations/logic/locationTypeRules';
import { formatLocationTypeLabel } from './locationTreeHelpers';

export interface CreatableLocationTypeSelectProps
  extends Omit<SelectProps, 'data' | 'value' | 'onChange'> {
  /** Options derived from existing location types (e.g. from collectLocationTypes) */
  options: { value: string; label: string; disabled?: boolean }[];
  value: string;
  onChange: (value: string) => void;
  /** When false, "Create …" is not offered for that typed value (placement rules). */
  isTypeAllowed?: (value: string) => boolean;
}

/**
 * A searchable Location Type select that allows choosing an existing type or creating a new one.
 * When the user types something that doesn't match any option, a "Create \"...\"" option is shown.
 * New values are trimmed only (not camelCased) and kept in `data` after the dropdown closes.
 */
export function CreatableLocationTypeSelect({
  options,
  value,
  onChange,
  isTypeAllowed,
  ...rest
}: CreatableLocationTypeSelectProps) {
  const [searchValue, setSearchValue] = useState('');
  /** Keeps a just-created value in `data` after search clears (Mantine blanks missing values). */
  const [committedCreate, setCommittedCreate] = useState<string | null>(null);

  const optionValues = useMemo(() => options.map((o) => o.value), [options]);

  // Clear only after parent adopts the value or the type appears in options.
  // Do NOT clear when value still lags behind (that caused create → wipe flicker).
  useEffect(() => {
    if (!committedCreate) return;
    const key = normalizeLocationTypeKey(committedCreate);
    const inOptions = options.some((o) => normalizeLocationTypeKey(o.value) === key);
    const parentAdopted = normalizeLocationTypeKey(value) === key;
    if (inOptions || parentAdopted) {
      setCommittedCreate(null);
    }
  }, [options, value, committedCreate]);

  const effectiveValue = committedCreate ?? value;

  const data = useMemo(() => {
    const base = options.map((o) => ({
      value: o.value,
      label: o.label,
      disabled: Boolean(o.disabled),
    }));

    const ensureValueInData = (raw: string, label: string, disabled: boolean) => {
      const key = normalizeLocationTypeKey(raw);
      if (!key) return;
      if (base.some((o) => normalizeLocationTypeKey(o.value) === key)) return;
      base.push({ value: raw, label, disabled });
    };

    if (effectiveValue) {
      const resolved =
        resolveLocationTypeInput(effectiveValue, optionValues) || effectiveValue.trim();
      const allowed = isTypeAllowed ? isTypeAllowed(resolved) : true;
      ensureValueInData(resolved, formatLocationTypeLabel(resolved), !allowed);
    }

    const searchTrimmed = searchValue.trim();
    if (searchTrimmed) {
      const resolved = resolveLocationTypeInput(searchTrimmed, optionValues);
      const isExisting = options.some(
        (o) => normalizeLocationTypeKey(o.value) === normalizeLocationTypeKey(searchTrimmed),
      );
      if (!isExisting && resolved) {
        const allowed = isTypeAllowed ? isTypeAllowed(resolved) : true;
        if (allowed) {
          const already = base.some(
            (o) => normalizeLocationTypeKey(o.value) === normalizeLocationTypeKey(resolved),
          );
          if (!already) {
            base.push({
              value: resolved,
              label: `Create "${searchTrimmed}"`,
              disabled: false,
            });
          } else if (
            !options.some(
              (o) => normalizeLocationTypeKey(o.value) === normalizeLocationTypeKey(resolved),
            )
          ) {
            const idx = base.findIndex(
              (o) => normalizeLocationTypeKey(o.value) === normalizeLocationTypeKey(resolved),
            );
            if (idx >= 0) {
              base[idx] = {
                ...base[idx],
                label: `Create "${searchTrimmed}"`,
                disabled: false,
              };
            }
          }
        }
      }
    }

    return base;
  }, [options, optionValues, effectiveValue, searchValue, isTypeAllowed]);

  const selectValue = useMemo(() => {
    if (!effectiveValue) return null;
    const key = normalizeLocationTypeKey(effectiveValue);
    const match = data.find((o) => normalizeLocationTypeKey(o.value) === key);
    return match?.value ?? effectiveValue.trim() ?? null;
  }, [effectiveValue, data]);

  const handleChange = useCallback(
    (v: string | null) => {
      if (!v) return;
      const trimmed = v.trim();
      if (!trimmed) return;

      const existing = options.find(
        (o) => normalizeLocationTypeKey(o.value) === normalizeLocationTypeKey(trimmed),
      );
      const next = existing ? existing.value : trimmed;

      const option = data.find(
        (o) => normalizeLocationTypeKey(o.value) === normalizeLocationTypeKey(next),
      );
      if (option?.disabled) return;
      if (isTypeAllowed && !isTypeAllowed(next)) return;

      if (!existing) {
        setCommittedCreate(next);
      } else {
        setCommittedCreate(null);
      }
      onChange(next);
      setSearchValue('');
    },
    [onChange, data, isTypeAllowed, options],
  );

  const handleDropdownClose = useCallback(() => {
    setSearchValue('');
  }, []);

  return (
    <Select
      {...rest}
      searchable
      data={data}
      value={selectValue}
      onChange={handleChange}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onDropdownClose={handleDropdownClose}
      nothingFoundMessage={null}
      allowDeselect={false}
    />
  );
}
